/*
 * Dashboard "Operaciones sin Facturar" (traspaso del HTML del Comité de Gerencia
 * a la coherencia visual del proyecto). Orquesta pestañas, toggles, filtros globales
 * y el modal de drill-down, consumiendo el dataset quemado embebido.
 */
import { useMemo, useState } from "react";
import { Body, Box, Button, Checkbox, Description, Flexbox, Label, Select, Stack, StackRow, Title } from "codekit";

import Funnelmd from "./Funnelmd";
import Tabclien from "./Tabclien";
import Tabdetal from "./Tabdetal";
import Tabimpor from "./Tabimpor";
import Tabresum from "./Tabresum";
import { carteraDataset } from "./utils/dataset";
import { carteraMeta, fmtCOP, prepareRows } from "./utils/carthelp";

const TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "importaciones", label: "Importaciones — Semáforo" },
  { id: "clientes", label: "Clientes y Directores" },
  { id: "detalle", label: "Detalle" },
];

/**
 * Construye las opciones de un filtro global con su placeholder
 * @param {Array} mData Datos base
 * @param {string} sKey Clave a listar
 * @param {string} sAll Texto de la opción "todos"
 * @returns {Array} Opciones [{ value, text }]
 */
const buildOptions = (mData, sKey, sAll) => {
  const mVals = [...new Set(mData.map((r) => r[sKey]))].filter(Boolean).sort();
  return [{ value: "", text: sAll }, ...mVals.map((v) => ({ value: v, text: v }))];
};

const Dashgraf = () => {
  const [sTab, setTab] = useState("resumen");
  const [bExcludeGlobal, setExcludeGlobal] = useState(true);
  const [bIncludeNegatives, setIncludeNegatives] = useState(false);
  const [sGerencia, setGerencia] = useState("");
  const [sTipo, setTipo] = useState("");
  const [mFunnel, setFunnel] = useState(null);

  //Normaliza el dataset una sola vez
  const mRaw = useMemo(() => prepareRows(carteraDataset), []);

  //Aplica exclusión de GLOBAL y negativos
  const mToggled = useMemo(() => {
    let mList = bExcludeGlobal ? mRaw.filter((r) => r["TIPO DE OPERACIÓN"] !== "GLOBAL") : mRaw;
    if (!bIncludeNegatives) mList = mList.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL" || r.VALOR >= 0);
    return mList;
  }, [mRaw, bExcludeGlobal, bIncludeNegatives]);

  //Aplica los filtros globales de gerencia y tipo
  const mData = useMemo(() => {
    let mList = mToggled;
    if (sGerencia) mList = mList.filter((r) => r.GERENCIA === sGerencia);
    if (sTipo) mList = mList.filter((r) => r["TIPO DE OPERACIÓN"] === sTipo);
    return mList;
  }, [mToggled, sGerencia, sTipo]);

  //Aviso de registros GLOBAL
  const mGlobalRows = useMemo(() => mRaw.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL"), [mRaw]);
  const nGlobalSum = mGlobalRows.reduce((a, r) => a + r.VALOR, 0);

  //Abre el modal de drill-down con el nivel inicial
  const openFunnel = (mScope, mKeys, mBreadcrumb) =>
    setFunnel({ scope: mScope, keys: mKeys, breadcrumb: mBreadcrumb });

  //Limpia los filtros globales
  const clearFilters = () => {
    setGerencia("");
    setTipo("");
  };

  return (
    <Box spacing={4}>
      <Box style={{ maxWidth: 1260, margin: "0 auto" }}>
      <Box spacingBottom={5}>
        <Title variant="title-1" color="accent-600">
          Operaciones sin Facturar
        </Title>
        <Description variant="description-2" color="neutral-500">
          Datos al {carteraMeta.generatedAt} · {mData.length.toLocaleString("es-CO")} DO pendientes
        </Description>
      </Box>

      <Box spacingBottom={5}>
        <Flexbox justifyContent="center" gap={1} wrap>
          {TABS.map((mT) => (
            <Button key={mT.id} type="button" size="m" variant={sTab === mT.id ? "secondary-bold" : "secondary"} onClick={() => setTab(mT.id)}>
              {mT.label}
            </Button>
          ))}
        </Flexbox>
      </Box>

      <Box bg="neutral-100" rounded="lg" border={1} borderColor="neutral-300" spacing={4} mb={4}>
        <Flexbox gap={6} alignItems="center" wrap>
          <Checkbox checked={bExcludeGlobal} onChange={(e) => setExcludeGlobal(e.target.checked)}>
            Excluir ajustes "GLOBAL"
          </Checkbox>
          <Checkbox checked={bIncludeNegatives} onChange={(e) => setIncludeNegatives(e.target.checked)}>
            Incluir valores negativos (notas crédito)
          </Checkbox>
        </Flexbox>
      </Box>

      <Box bg="neutral-100" rounded="lg" border={1} borderColor="neutral-300" spacing={4} mb={4}>
        <Stack gap={3} columnsVariant="form-3">
          <StackRow>
            <Label>Gerencia</Label>
            <Select options={buildOptions(mToggled, "GERENCIA", "Todas las gerencias")} value={sGerencia} onChangeValue={({ option }) => setGerencia(option.value)} />
          </StackRow>
          <StackRow>
            <Label>Tipo de Operación</Label>
            <Select options={buildOptions(mToggled, "TIPO DE OPERACIÓN", "Todos los tipos")} value={sTipo} onChangeValue={({ option }) => setTipo(option.value)} />
          </StackRow>
          <StackRow>
            <Label>&nbsp;</Label>
            <Button type="button" variant="secondary" size="m" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </StackRow>
        </Stack>
      </Box>

      {mGlobalRows.length ? (
        <Box bg="warning-100" rounded="md" spacing={4} mb={4}>
          <Body variant="body-3" color="warning-800">
            Los registros tipo GLOBAL ({mGlobalRows.length}) suman {fmtCOP(nGlobalSum)} y pueden distorsionar el neto — revísalos antes de reportar.
          </Body>
        </Box>
      ) : null}

      {sTab === "resumen" && <Tabresum data={mData} onFunnel={openFunnel} />}
      {sTab === "importaciones" && <Tabimpor data={mData} onFunnel={openFunnel} />}
      {sTab === "clientes" && <Tabclien data={mData} onFunnel={openFunnel} />}
      {sTab === "detalle" && <Tabdetal data={mData} />}

      {mFunnel ? <Funnelmd initial={mFunnel} onClose={() => setFunnel(null)} /> : null}
      </Box>
    </Box>
  );
};

export default Dashgraf;
